import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  constructor(private http: HttpClient) {}

  private userService = environment.userService + '/v1';

  //********** User  APIs ************

  checkEmail(payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + '/staff/checkEmail', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getEmailData(payload: any): Observable<any> {
    return this.http.post<any>(this.userService + '/staff/get', payload).pipe(catchError((err) => throwError(err)));
  }

  getStaffDetails(payload: any): Observable<any> {
    return this.http.post<any>(this.userService + '/staff/get', payload).pipe(catchError((err) => throwError(err)));
  }

  changePassword(payload: any): Observable<any> {
    return this.http
      .put<any>(this.userService + '/staff/change-password', payload)
      .pipe(catchError((err) => throwError(err)));
  }
  addUser(reqObj: any): Observable<any> {
    return this.http.post<any>(this.userService + '/user/register', reqObj).pipe(catchError((err) => throwError(err)));
  }
  editUser(reqObj: any): Observable<any> {
    return this.http.post<any>(this.userService + '/user/update', reqObj).pipe(catchError((err) => throwError(err)));
  }
  // `createUser()` removed 2026-08-14 (security audit SA-01). It posted to
  // /staff/signUp, an unauthenticated route that mass-assigned the body into the
  // staff model; that route has been deleted server-side. Nothing called this
  // method — merchant onboarding creates its first staff member via
  // createTeamMember() -> /team/upsert, and the authenticated path is /staff/create.

  updateUser(payload: any): Observable<any> {
    return this.http.put<any>(this.userService + `/staff/update`, payload).pipe(catchError((err) => throwError(err)));
  }

  // `loginUser()` removed 2026-08-14 (security audit SA-02). It posted to
  // /staff/login, which returned the whole staff document including the bcrypt
  // password hash; that route has been deleted server-side. Nothing called this —
  // the admin authenticates through /user/email/login.

  checkUserExist(payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/user/check-email`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  checkUserNameExist(payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/shop/check-userName-exists`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  sendOtp(payload: any): Observable<any> {
    return this.http.post<any>(this.userService + `/team/send-otp`, payload).pipe(catchError((err) => throwError(err)));
  }

  verifyOtp(payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/team/verify-otp`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  createShop(payload: any): Observable<any> {
    return this.http.post<any>(this.userService + `/shop/add-shop`, payload).pipe(catchError((err) => throwError(err)));
  }

  getShopById(id: string): Observable<any> {
    return this.http.get<any>(this.userService + `/shop/fetch-shop/${id}`).pipe(catchError((err) => throwError(err)));
  }

  updateShop(payload: any): Observable<any> {
    return this.http
      .put<any>(this.userService + `/shop/update-shop?id=${payload._id}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  checkShopNameExist(payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/shop/check-userName-exists`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getShopListByIds(payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/shop/get-shop-list`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getShopList(payload: any, page: any, pageSize: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/shop/get-shop?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  deleteShop(id: any): Observable<any> {
    return this.http
      .delete<any>(this.userService + `/shop/delete-shop?id=${id}`)
      .pipe(catchError((err) => throwError(err)));
  }

  getTotalShopCount(payload: any): Observable<any> {
    return this.http.post<any>(this.userService + `/shop/total`, payload).pipe(catchError((err) => throwError(err)));
  }

  getShopSeoList(shopId: string): Observable<any> {
    return this.http.get<any>(this.userService + `/shop-seo/${shopId}`).pipe(catchError((err) => throwError(err)));
  }

  getShopSeoById(id: string): Observable<any> {
    return this.http.get<any>(this.userService + `/shop-seo/by-id/${id}`).pipe(catchError((err) => throwError(err)));
  }

  // channelId (optional) resolves the channel SEO override, else the shop default (Channel Partner Phase 1).
  getShopSeoByPage(shopId: string, pageIdentifier: string, channelId?: string): Observable<any> {
    const qs = channelId ? `?channelId=${channelId}` : '';
    return this.http
      .get<any>(this.userService + `/shop-seo/${shopId}/page/${pageIdentifier}${qs}`)
      .pipe(catchError((err) => throwError(err)));
  }

  // Pass channelId in the payload to upsert a channel-scoped SEO override (never the shop row).
  upsertShopSeo(shopId: string, pageIdentifier: string, payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/shop-seo/${shopId}/page/${pageIdentifier}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // Revert a channel's SEO override(s) so it inherits the shop default again.
  revertChannelSeo(shopId: string, channelId: string, pageIdentifier?: string): Observable<any> {
    return this.http
      .post<any>(this.userService + `/shop-seo/${shopId}/channel-override/revert`, { channelId, pageIdentifier })
      .pipe(catchError((err) => throwError(err)));
  }

  deactivateShopSeo(shopId: string, pageIdentifier: string): Observable<any> {
    return this.http
      .delete<any>(this.userService + `/shop-seo/${shopId}/page/${pageIdentifier}`)
      .pipe(catchError((err) => throwError(err)));
  }

  // Location

  getLocation(payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/location/get-location`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  addLocation(payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/location/add-location`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  updateLocation(payload: any): Observable<any> {
    return this.http
      .put<any>(this.userService + `/location/update-location?id=${payload._id}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  deleteLocation(payload: any): Observable<any> {
    return this.http
      .delete<any>(this.userService + `/location/delete-location?id=${payload._id}`)
      .pipe(catchError((err) => throwError(err)));
  }

  //********** Extra Charge  APIs ************

  createExtraCharge(payload: any): Observable<any> {
    return this.http.post<any>(this.userService + '/extra-charge', payload).pipe(catchError((err) => throwError(err)));
  }

  getAllExtraCharge(payload: any, page: any, pageSize: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/extra-charge/get?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getExtraChargeById(payload: any): Observable<any> {
    return this.http
      .get<any>(this.userService + `/extra-charge/get/${payload.chargeId}`)
      .pipe(catchError((err) => throwError(err)));
  }

  updateExtraCharge(payload: any): Observable<any> {
    return this.http.put<any>(this.userService + '/extra-charge', payload).pipe(catchError((err) => throwError(err)));
  }

  deleteExtraCharge(deleteId: any): Observable<any> {
    return this.http
      .delete<any>(this.userService + '/extra-charge/' + deleteId)
      .pipe(catchError((err) => throwError(err)));
  }

  //********** Event  APIs ************

  createEvent(payload: any): Observable<any> {
    return this.http.post<any>(this.userService + '/event', payload).pipe(catchError((err) => throwError(err)));
  }

  getAllEvent(payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + '/event/get?page=1&pageSize=10', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  deleteEvent(deleteId: any): Observable<any> {
    return this.http.delete<any>(this.userService + '/event/' + deleteId).pipe(catchError((err) => throwError(err)));
  }

  getEventById(editId: any): Observable<any> {
    return this.http.get<any>(this.userService + '/event/get/' + editId).pipe(catchError((err) => throwError(err)));
  }

  updateEvent(payload: any): Observable<any> {
    return this.http.put<any>(this.userService + '/event', payload).pipe(catchError((err) => throwError(err)));
  }

  //********** Pincode WhiteList  APIs ************

  addPincode(payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + '/pincodeWhitelist/add-pincode', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getAllPincode(payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + '/pincodeWhitelist/get-pincode', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  deletePincode(id: any): Observable<any> {
    return this.http
      .delete<any>(this.userService + '/pincodeWhitelist/delete-pincode/' + id)
      .pipe(catchError((err) => throwError(err)));
  }

  //Role

  createRole(payload: any): Observable<any> {
    return this.http.post<any>(this.userService + '/role', payload).pipe(catchError((err) => throwError(err)));
  }

  getAllRole(payload: any, page: any, pageSize: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/role/get?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  deleteRole(deleteId: any): Observable<any> {
    return this.http.delete<any>(this.userService + '/role/' + deleteId).pipe(catchError((err) => throwError(err)));
  }

  getRoleById(editId: any): Observable<any> {
    return this.http.get<any>(this.userService + '/role/' + editId).pipe(catchError((err) => throwError(err)));
  }

  updateRole(payload: any): Observable<any> {
    return this.http.put<any>(this.userService + '/role', payload).pipe(catchError((err) => throwError(err)));
  }

  getUserById(id: any): Observable<any> {
    return this.http.get(this.userService + `/user/fetch/${id}`).pipe(catchError((err) => throwError(err)));
  }

  getModuleByRole(payload: any, page: any, pageSize: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/role/get?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  checkRoleExists(payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/role/check-exists`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // Team member
  deleteTeamMember(id: any, shopId: any): Observable<any> {
    return this.http
      .delete<any>(this.userService + `/team/${id}?shop_id=${shopId}`)
      .pipe(catchError((err) => throwError(err)));
  }

  getTeamMember(payload: any, queryObj: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/team/get?page=${queryObj.page}&pageSize=${queryObj.pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getAllTeamMembers(payload: any): Observable<any> {
    return this.http.post<any>(this.userService + `/team/get`, payload).pipe(catchError((err) => throwError(err)));
  }

  getTeamMemberById(id: any): Observable<any> {
    return this.http.get<any>(this.userService + `/team/get/${id}`).pipe(catchError((err) => throwError(err)));
  }

  switchShop(shopId: string): Observable<any> {
    return this.http
      .post<any>(this.userService + `/team/switch-shop`, { shop_id: shopId })
      .pipe(catchError((err) => throwError(err)));
  }

  checkTeamMemberExistByPhone(payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/team/check-mobile`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  checkTeamMemberExist(payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/team/check-email`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  createTeamMember(payload: any): Observable<any> {
    return this.http.post<any>(this.userService + '/team/upsert', payload).pipe(catchError((err) => throwError(err)));
  }

  updateTeamMember(payload: any): Observable<any> {
    return this.http.put<any>(this.userService + `/team`, payload).pipe(catchError((err) => throwError(err)));
  }

  checkTeamMemberExistByEmailPhone(payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/team/check-email-mobile`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // pickup

  getAllPickupLocations(payload: any, page: any, pageSize: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/pickup-center/get?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  addPickupLocation(payload: any, queryParams: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/pickup-center`, payload, { params: queryParams })
      .pipe(catchError((err) => throwError(err)));
  }

  checkLocationExists(payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/pickup-center/check-exists`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getLocationById(id: any): Observable<any> {
    return this.http.get<any>(this.userService + `/pickup-center/${id}`).pipe(catchError((err) => throwError(err)));
  }

  geVendorsList(payload: any, page: number, pageSize: number): Observable<any> {
    return this.http
      .post<any>(this.userService + `/vendor/get-vendor?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  public addVendor(payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/vendor/add-vendor`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  public updateVendor(payload: any): Observable<any> {
    return this.http
      .put<any>(this.userService + `/vendor/update-vendor`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  public deleteVendor(id: any): Observable<any> {
    return this.http
      .delete<any>(this.userService + `/vendor/delete-vendor/${id}`)
      .pipe(catchError((err) => throwError(err)));
  }

  public getVendorById(id: any): Observable<any> {
    return this.http.get<any>(this.userService + `/vendor/get-vendor/${id}`).pipe(catchError((err) => throwError(err)));
  }

  public getSecretKey(payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/shop-secret/get`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  // Check PinCode
  getPincodeData(pincode: any) {
    return this.http.get(`https://api.postalpincode.in/pincode/${pincode}`).pipe(
      catchError((error: any) => {
        return throwError(() => error);
      })
    );
  }
  // Shop Locator

  addShopLocator(payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + `/shop-locator/add-shop-locator`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  updateShopLocator(id: string, payload: any): Observable<any> {
    return this.http
      .put<any>(this.userService + `/shop-locator/update-shop-locator/${id}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  getShopLocatorById(id: string): Observable<any> {
    return this.http
      .get<any>(this.userService + `/shop-locator/fetch-shop-locator/${id}`)
      .pipe(catchError((err) => throwError(err)));
  }

  getAllShopLocators(params: any): Observable<any> {
    return this.http
      .get<any>(this.userService + `/shop-locator/get-all-shop-locators`, { params })
      .pipe(catchError((err) => throwError(err)));
  }

  getPickupAvailability(shopId: string): Observable<any> {
    return this.http
      .get<any>(this.userService + `/shop-locator/pickup-availability`, { params: { shopId } })
      .pipe(catchError((err) => throwError(err)));
  }

  deleteShopLocator(id: string): Observable<any> {
    return this.http
      .delete<any>(this.userService + `/shop-locator/delete-shop-locator/${id}`)
      .pipe(catchError((err) => throwError(err)));
  }

  //********** GST APIs ************

  getGstList(payload: any, page = 1, pageSize = 10): Observable<any> {
    return this.http
      .post<any>(this.userService + `/gst/get?page=${page}&pageSize=${pageSize}`, payload)
      .pipe(catchError((err) => throwError(err)));
  }

  createGst(payload: any): Observable<any> {
    return this.http.post<any>(this.userService + '/gst', payload).pipe(catchError((err) => throwError(err)));
  }

  updateGst(payload: any): Observable<any> {
    return this.http.put<any>(this.userService + '/gst', payload).pipe(catchError((err) => throwError(err)));
  }

  deleteGst(id: string): Observable<any> {
    return this.http.delete<any>(this.userService + `/gst/${id}`).pipe(catchError((err) => throwError(err)));
  }

  checkGstExists(payload: any): Observable<any> {
    return this.http
      .post<any>(this.userService + '/gst/check-exists', payload)
      .pipe(catchError((err) => throwError(err)));
  }

  exportGst(shopId: string): Observable<Blob> {
    return this.http
      .get(this.userService + `/gst/export?shop_id=${shopId}`, { responseType: 'blob' })
      .pipe(catchError((err) => throwError(err)));
  }
}
